import { getDownloadURL, getMetadata, ref } from "firebase/storage";
import { useEffect, useState } from "react";
import { storage } from "src/firebase";

export type CvFile = {
  url: string;
  /**
   * What Storage will serve the object as.
   *
   * Read from the object, never inferred from the file name. The two disagree
   * on files already in the bucket: `taaly-dev` holds a `cv_1789396388329.pdf`
   * stored as `application/octet-stream`, written before `taaly_mobile`'s
   * uploader learned document types. The name is the uploader's claim; this is
   * what the browser will act on.
   */
  contentType: string;
  bytes: number;
  /**
   * When the object was written, as an ISO string.
   *
   * From Storage rather than from a Firestore row, so it is available for a CV
   * the upload ledger has no row for — every file uploaded before that ledger
   * existed. It is what lets the page say "uploaded on" about a CV it cannot
   * tie to a conversation.
   */
  uploadedAt: string;
};

/**
 * The learner's CV, with enough about it to say what it is before it is opened.
 *
 * Deliberately not `useDownloadUrl`: that hook is shared with coach recordings,
 * which need a url and nothing else, and widening it would make every consumer
 * pay for a metadata read none of them asked for.
 *
 * Both reads are awaited together because they fail together — an admin without
 * the `job_cvs` Storage rule is refused on each, so a partial result would be a
 * page that claims to know the file and cannot open it.
 */
export const useCvFile = (path?: string) => {
  const [file, setFile] = useState<CvFile>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!path) {
      setFile(undefined);
      setLoading(false);

      return;
    }

    let current = true;

    setLoading(true);

    const fetchFile = async () => {
      try {
        const fileRef = ref(storage, path);
        const [url, metadata] = await Promise.all([getDownloadURL(fileRef), getMetadata(fileRef)]);

        if (!current) return;

        setFile({
          url,
          contentType: metadata.contentType ?? "",
          bytes: Number(metadata.size ?? 0),
          uploadedAt: metadata.timeCreated ?? "",
        });
      } catch (error) {
        console.error("Failed to load the CV:", error);

        if (current) setFile(undefined);
      } finally {
        if (current) setLoading(false);
      }
    };

    fetchFile();

    // The path changes when the reader moves to another record without the
    // detail view unmounting, so a slow first read must not overwrite a fast
    // second one.
    return () => {
      current = false;
    };
  }, [path]);

  return { file, loading };
};
